import { Random } from '../utils/Random.js';

/**
 * Random short glitch pulses (distortion, flicker, brightness).
 */
export class GlitchController {
  /**
   * @param {import('./GraphicsSettings.js').GraphicsSettings} settings
   * @param {{ minInterval?: number; maxInterval?: number; seed?: number }} [options]
   */
  constructor(settings, options = {}) {
    this.settings = settings;
    this._rng = new Random(options.seed ?? 0x6a1a7e);
    this._minInterval = options.minInterval ?? 10;
    this._maxInterval = options.maxInterval ?? 30;

    this.strength = 0;
    this.brightness = 1;
    this.flicker = 0;

    this._wait = this._nextInterval();
    this._remaining = 0;

    /** @type {(() => void) | null} */
    this.onTrigger = null;
  }

  /** @param {number} deltaSeconds */
  update(deltaSeconds) {
    if (!this.settings.glitch) {
      this.strength = 0;
      this.flicker = 0;
      this.brightness = 1;
      return;
    }

    if (this._remaining > 0) {
      this._remaining -= deltaSeconds;
      const t = Math.max(0, this._remaining / this._duration);
      this.strength = this._peakStrength * t;
      this.flicker = this._peakFlicker * t;
      this.brightness = 1 + (this._peakBrightness - 1) * t;
      if (this._remaining <= 0) {
        this.strength = 0;
        this.flicker = 0;
        this.brightness = 1;
        this._wait = this._nextInterval();
      }
      return;
    }

    this._wait -= deltaSeconds;
    if (this._wait <= 0) {
      this._trigger();
    }
  }

  _nextInterval() {
    return this._rng.range(this._minInterval, this._maxInterval);
  }

  _trigger() {
    this._duration = this._rng.range(0.1, 0.28);
    this._remaining = this._duration;
    this._peakStrength = this._rng.range(0.25, 0.55);
    this._peakFlicker = this._rng.range(0.15, 0.45);
    this._peakBrightness = this._rng.range(0.97, 1.03);
    if (this.onTrigger) this.onTrigger();
  }

  /**
   * Strong screen pulse (world events / scripted glitches).
   * @param {number} durationSeconds
   * @param {number} [peakStrength]
   */
  forcePulse(durationSeconds, peakStrength = 0.75) {
    if (!this.settings.glitch) return;
    this._duration = durationSeconds;
    this._remaining = durationSeconds;
    this._peakStrength = peakStrength;
    this._peakFlicker = Math.min(1, peakStrength * 0.85);
    this._peakBrightness = 1.04;
    if (this.onTrigger) this.onTrigger();
  }
}
